<template>
  <div
    v-if="current"
    :class="['r3-system-notice', { 'is-icon-only': iconOnly }]"
    :style="colorStyle"
  >
    <!-- just the icon: collapsed by the user, or a small screen. Clicking it
         expands the banner, or on a small screen opens the popup. -->
    <button
      v-if="iconOnly"
      type="button"
      class="r3-system-notice__body"
      :title="`Show notice: ${fullText}`"
      @click="isSmallScreen ? (popupOpen = true) : setCollapsed(false)"
    >
      <i :class="iconClass"></i>
    </button>

    <div v-else class="r3-system-notice__body" role="alert" :title="fullText">
      <i :class="iconClass"></i>
      <div class="r3-system-notice__text">
        <span v-for="p in parts" :key="p.cls" :class="p.cls">{{ p.text }}</span>
      </div>
      <DxButton icon="close" styling-mode="text" hint="Minimize" @click="setCollapsed(true)" />
    </div>

    <R3SystemNoticePopup
      v-if="isSmallScreen"
      :visible="popupOpen"
      :notice="popupNotice"
      @close="popupOpen = false"
    />
  </div>
</template>

<script>
// Banner for upcoming updates / scheduled maintenance, shown in the center of
// the header toolbar (header-toolbar.vue).
// On small screens only the icon fits in the header; tapping it opens the full
// notice in R3SystemNoticePopup.
//
// Dates are shown in the logged-in user's profile timezone (users/getProfile ->
// timeZone), falling back to the browser's timezone if the profile has none.
import { DxButton } from "devextreme-vue/button";
import R3SystemNoticePopup from "./R3SystemNoticePopup.vue";

// Each notice's icon and color come from its type in the database
// (system_notice_type); a notice's own color replaces the type's. These are
// only used if that data is missing or the color isn't a valid hex code.
const DEFAULT_ICON = "info";
const DEFAULT_COLOR = "#1e6fd9";

// how often to re-fetch the notices, so new ones appear without a reload
const REFRESH_MS = 5 * 60 * 1000;

// "#f80" or "#ff8800"
const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

// Dark text on light backgrounds, white on dark ones, so any background color
// stays readable.
function textColorFor(hex) {
  let digits = hex.slice(1);
  if (digits.length === 3) digits = [...digits].map((d) => d + d).join("");
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(digits.slice(i, i + 2), 16));
  const brightness = (r * 299 + g * 587 + b * 114) / 1000; // 0-255
  return brightness >= 150 ? "rgba(0, 0, 0, 0.87)" : "#fff";
}

// below this the header only has room for the icon
const SMALL_SCREEN = window.matchMedia("(max-width: 767.98px)");

// ids of notices the user has collapsed to just the icon, so they stay
// collapsed across page changes and reloads
const COLLAPSED_KEY = "r3SystemNoticesCollapsed";

function readCollapsed() {
  try {
    return JSON.parse(localStorage.getItem(COLLAPSED_KEY)) || [];
  } catch (e) {
    // private window / blocked storage -- notices just start expanded
    return [];
  }
}

export default {
  components: { DxButton, R3SystemNoticePopup },
  data() {
    return {
      now: Date.now(),
      timer: null,
      refreshTimer: null,
      collapsed: readCollapsed(),
      isSmallScreen: SMALL_SCREEN.matches,
      popupOpen: false,
    };
  },
  computed: {
    // Of the notices showing right now: the most important type first (lowest
    // typeSort, from system_notice_type.sort), then the one starting soonest.
    // Each comparison returns 0 on a tie, so `||` moves on to the next one.
    current() {
      return this.$store.getters["systemNotices/list"]
        .filter((n) => this.now >= Date.parse(n.showFrom) && this.now <= Date.parse(n.showUntil))
        .sort((a, b) =>
          (a.typeSort - b.typeSort) ||
          (Date.parse(a.startsAt) - Date.parse(b.startsAt)) ||
          (a.id - b.id)
        )[0] || null;
    },
    iconOnly() {
      return this.isSmallScreen || this.collapsed.includes(this.current.id);
    },
    iconClass() {
      return ["dx-icon", `dx-icon-${this.current.icon || DEFAULT_ICON}`, "r3-system-notice__icon"];
    },
    colorStyle() {
      // the notice's own color, else its type's color -- whichever is a valid hex code
      const background = [this.current.color, this.current.typeColor]
        .find((c) => HEX_COLOR.test(c)) || DEFAULT_COLOR;
      return { background, color: textColorFor(background) };
    },
    timeZone() {
      const profileTz = this.$store.getters["users/getProfile"]?.timeZone;
      if (profileTz) {
        try {
          // Intl throws RangeError on an unknown timezone id
          new Intl.DateTimeFormat("en-US", { timeZone: profileTz });
          return profileTz;
        } catch (e) { /* fall through to the browser timezone */ }
      }
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    },
    dateText() {
      if (!this.current.startsAt) return "";
      const start = new Date(this.current.startsAt);
      const end = this.current.endsAt ? new Date(this.current.endsAt) : null;
      const full = {
        weekday: "short", month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit",
      };
      const time = { hour: "numeric", minute: "2-digit" };

      if (!end) return this.format(start, { ...full, timeZoneName: "short" });

      const sameDay = this.format(start, { dateStyle: "short" }) === this.format(end, { dateStyle: "short" });
      return `${this.format(start, full)} – ${this.format(end, { ...(sameDay ? time : full), timeZoneName: "short" })}`;
    },
    // pieces of the notice text, in display order (empty ones left out)
    parts() {
      return [
        { cls: "r3-system-notice__title", text: this.current.title },
        { cls: "r3-system-notice__date", text: this.dateText },
        { cls: "r3-system-notice__message", text: this.current.message },
      ].filter((p) => p.text);
    },
    fullText() {
      return this.parts.map((p) => p.text).join(" · ");
    },
    popupNotice() {
      return {
        title: this.current.title,
        message: this.current.message,
        dateText: this.dateText,
        iconClass: this.iconClass,
        colorStyle: this.colorStyle,
      };
    },
  },
  mounted() {
    // the header only exists once logged in, so the token is there by now
    this.$store.dispatch("users/loadProfile").catch(() => {
      // banner still works with the browser timezone
    });
    this.loadNotices();
    this.refreshTimer = setInterval(this.loadNotices, REFRESH_MS);
    // re-check showFrom / showUntil every minute so notices appear and expire
    // without a page reload
    this.timer = setInterval(() => { this.now = Date.now(); }, 60 * 1000);
    SMALL_SCREEN.addEventListener("change", this.onScreenChange);
  },
  beforeUnmount() {
    clearInterval(this.timer);
    clearInterval(this.refreshTimer);
    SMALL_SCREEN.removeEventListener("change", this.onScreenChange);
  },
  methods: {
    loadNotices() {
      this.$store.dispatch("systemNotices/loadActive").catch(() => {
        // keep showing whatever was loaded last; a failed refresh isn't worth
        // an error message for a banner
      });
    },
    onScreenChange(e) {
      this.isSmallScreen = e.matches;
      if (!e.matches) this.popupOpen = false;
    },
    format(date, options) {
      return new Intl.DateTimeFormat("en-US", { ...options, timeZone: this.timeZone }).format(date);
    },
    setCollapsed(value) {
      const id = this.current.id;
      this.collapsed = value
        ? [...this.collapsed, id]
        : this.collapsed.filter((c) => c !== id);
      try {
        localStorage.setItem(COLLAPSED_KEY, JSON.stringify(this.collapsed));
      } catch (e) { /* storage unavailable -- state lasts until reload */ }
    },
  },
};
</script>

<style scoped>
/* As wide as the message, up to a max. The toolbar centers this item, so the
   max leaves room for the wider side of the header (menu button + logo +
   title, ~230px) on both sides, plus a gap. */
.r3-system-notice {
  width: max-content;
  max-width: calc(100vw - 520px);
  height: 44px;
  border-radius: 4px;
}

.r3-system-notice.is-icon-only {
  width: 44px;
  max-width: none; /* the calc above goes negative on small screens */
}

.r3-system-notice__body {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 100%;
  padding: 0 4px 0 12px;
}

.is-icon-only .r3-system-notice__body {
  justify-content: center;
  width: 100%;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  cursor: pointer;
}

/* the close button and its icon, which the DevExtreme theme colors itself */
.r3-system-notice__body :deep(.dx-button),
.r3-system-notice__body :deep(.dx-button .dx-icon) {
  color: inherit;
}

.r3-system-notice__icon {
  font-size: 20px;
  flex-shrink: 0;
}

/* up to two lines to fit the header; anything more is cut off and the full
   text is in the hover tooltip */
.r3-system-notice__text {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  line-height: 1.3;
  max-height: 2.6em; /* 2 lines */
  overflow: hidden;
}

.r3-system-notice__text > span + span::before {
  content: "·";
  margin: 0 8px;
  opacity: 0.7;
}

.r3-system-notice__title {
  font-weight: 600;
}

.r3-system-notice__message {
  opacity: 0.9;
}
</style>
